import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormItem, Select } from 'Components';
import { useFormikContext } from 'formik';
import { AddUserValues } from 'Pages/User/User.types';
import { translateTInfo } from 'i18n/i18n.helpers';
import { Group } from 'Services/Api/Groups/Types';
import { useCustomTenantRolesDropdownList } from '../EditUser/tabs/ModuleSettings/ModuleSettingsContainer';

const FIELD_NAME: keyof AddUserValues = 'customTenantRoleIds';

interface BaseUserFormValues {
	peakV3Id: string;
	groups: Group[];
	customTenantRoleIds?: Group[];
}

interface CustomTenantRoleFieldProps {
	onLoadingChange?: (loading: boolean) => void;
}

function CustomTenantRoleField<T extends BaseUserFormValues>({
	onLoadingChange
}: CustomTenantRoleFieldProps) {
	const { t } = useTranslation();

	const {
		values,
		setFieldValue,
		getFieldMeta,
		setFieldTouched
	} = useFormikContext<T>();

	const meta = getFieldMeta<number[]>(FIELD_NAME);

	const previousGroups = useRef(values.groups);

	useEffect(() => {
		if (previousGroups.current !== values.groups) {
			previousGroups.current = values.groups;
		}
	}, [values.groups, setFieldValue]);

	const onBlur = () => {
		setFieldTouched(FIELD_NAME, true);
	};

	const error = meta.touched ? meta.error : undefined;

	const mappedGroups = useMemo(
		() =>
			values.groups?.map((gp: any) => ({
				key: gp.key,
				value: gp.key,
				label: gp.label,
				data: gp.data
			})) || [],
		[values.groups]
	);

	const { customTenantRolesDropdownList, loading } =
		useCustomTenantRolesDropdownList(mappedGroups);

	useEffect(() => {
		onLoadingChange?.(loading);
	}, [loading, onLoadingChange]);

	const dropdownOptions = customTenantRolesDropdownList.map(
		(role) => ({
			key: role.id,
			value: role.id,
			label: role.name,
			data: {
				id: role.id,
				moduleSettings: role.moduleSettings
			}
		})
	);

	const mergedOptions = dropdownOptions;

	if (loading || mergedOptions.length === 0) {
		return null;
	}

	return (
		<FormItem
			label={t('user.tenantRoles')}
			validateStatus={(error && 'error') || undefined}
			help={translateTInfo(error, t)}
		>
			<Select
				mode="multiple"
				onChange={(value) => setFieldValue(FIELD_NAME, value)}
				value={values?.customTenantRoleIds}
				optionFilterProp="children"
				onBlur={onBlur}
			>
				{mergedOptions.map((role) => (
					<Select.Option
						key={role.key}
						value={role.value}
					>
						{role.label}
					</Select.Option>
				))}
			</Select>
		</FormItem>
	);
}

export default CustomTenantRoleField;